import React from 'react';
import PropTypes from 'prop-types';
import { chunk } from 'lodash';
import { generateSectionId } from '@repo/ui-components/dept/utils/generateSectionId';

import {
  Container,
  Box,
  Flex,
  Text,
  VStack,
  GridItem,
  useBreakpointValue,
  useTheme,
} from '@chakra-ui/react';

import Image from '@repo/ui-components/tailwind/Image';
import DeptGrid from '@repo/ui-components/dept/design-system/DeptGrid';
import Logo from '@repo/ui-components/dept/atoms/Logo';
import StatCard from '@repo/ui-components/dept/atoms/StatCard';

export default function EditorialStatGrid({ block }) {
  const { headline, cards } = block;
  const theme = useTheme();

  const smallVersion = cards?.length === 3;

  const chunkSize = useBreakpointValue({
    base: smallVersion ? 2 : 3,
    lg: smallVersion ? 1 : 2,
  });
  const cardsArray = chunk(cards, chunkSize);

  return (
    <Box
      {...generateSectionId(block)}
      pos="relative"
      bgGradient={{
        base: 'linear(brand.ink 0%, brand.ink 100%)',
        lg: 'linear(brand.ink 37rem, transparent 37rem)',
      }}
    >
      <Container py={20}>
        <Flex
          justify="space-between"
          mb={{
            base: 10,
            lg: 0,
          }}
        >
          <VStack align="start" w="41.8rem" gap={4}>
            <Logo yellow />
            <Text as="h2" textStyle="h1" color="typography.lighter">
              {headline}
            </Text>
          </VStack>
          <Image
            className="hidden lg:block"
            src={theme.images.unleashJoyCompact}
            width="18.8rem"
          />
        </Flex>

        <DeptGrid columnGap={6} rowGap={16}>
          <GridItem
            display={{ base: 'block', md: 'none' }}
            colSpan={{
              base: 6,
              md: 3,
              lg: 4,
            }}
            key="unleashJoyCompact"
            order={1}
          >
            <Image
              alt="Unleash joy compact image"
              className="mx-auto"
              src={theme.images.unleashJoyCompact}
              width="18.8rem"
            />
          </GridItem>

          {cardsArray.map((cardsGroup, index) => (
            <GridItem
              key={cardsGroup[0]._key}
              colSpan={{
                base: 6,
                md: 3,
                lg: 4,
              }}
              mt={{ lg: `${index * 2.5}rem` }}
              as={VStack}
              spacing={16}
              order={index * 2}
            >
              {cardsGroup.map(card => (
                <StatCard key={card._key} card={card} />
              ))}
            </GridItem>
          ))}
        </DeptGrid>
      </Container>
    </Box>
  );
}

EditorialStatGrid.propTypes = {
  block: PropTypes.shape({
    _type: PropTypes.string.isRequired,
    headline: PropTypes.string.isRequired,
    cards: PropTypes.arrayOf(
      PropTypes.shape({
        _key: PropTypes.string.isRequired,
        title: PropTypes.string,
        stat: PropTypes.string,
        description: PropTypes.string,
        cta: PropTypes.shape({
          _key: PropTypes.string,
          label: PropTypes.string,
          link: PropTypes.shape({
            _type: PropTypes.string,
            slug: PropTypes.shape({
              current: PropTypes.string,
            }),
          }),
        }),
      }).isRequired
    ).isRequired,
  }).isRequired,
};
